import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { MEModal } from '@CommonComponents'
import { toast } from '@CommonComponents/toast'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { codeOrderManagement as endpoints } from '@URLS'

const LOOKUP_DEBOUNCE_MS = 400

// Errors from the lookup and from a refused change are shown inside the modal rather than
// as a toast: they run long, they name specific codes, and the admin needs to read them
// with the form still open so they can correct the course number.
const readError = err =>
  err?.response?.data?.error || err?.message || String(err)

/**
 * Move an access code order, and every code in it, to a different master course.
 *
 * The course number is typed rather than picked from a list because some master courses are
 * deliberately kept out of the catalog and still have to be selectable. That makes the
 * as-you-type lookup the only feedback the admin gets before committing, so it also carries
 * the impact summary (how many codes move, how many are expired or returned, whether the
 * move actually changes anything).
 *
 * The lookup is advisory only -- the server re-runs every check inside its own lock before
 * it writes, so a stale preview is refused rather than trusted.
 */
export const ChangeCourseModal = ({
  show,
  close,
  orderId,
  coursenum,
  courseTitle,
}) => {
  let [, dispatch] = useStateValue()

  let [input, setInput] = useState('')
  let [reason, setReason] = useState('')
  let [preview, setPreview] = useState(null)
  let [checking, setChecking] = useState(false)
  let [saving, setSaving] = useState(false)
  let [error, setError] = useState('')

  // Ignore a lookup that resolves after the admin has typed again, so a slow early request
  // can't overwrite the answer for what's currently in the box.
  let latestLookup = useRef(0)

  const resetState = useCallback(() => {
    setInput('')
    setReason('')
    setPreview(null)
    setChecking(false)
    setSaving(false)
    setError('')
  }, [])

  useEffect(() => {
    if (show) {
      resetState()
    }
  }, [show, resetState])

  // Fetch the impact summary even before a course number is typed, so a blocked order says
  // so immediately instead of waiting for the admin to pick a target they can't move to.
  useEffect(() => {
    if (!show) {
      return
    }
    const token = ++latestLookup.current
    const trimmed = input.trim()
    setChecking(!!trimmed)

    const timer = setTimeout(
      async () => {
        try {
          let resp = await client.get(
            endpoints.checkCourseChange({
              orderId: orderId,
              coursenum: trimmed,
            })
          )
          if (token === latestLookup.current) {
            setPreview(resp.data)
            setError('')
          }
        } catch (err) {
          if (token === latestLookup.current) {
            setPreview(null)
            setError(readError(err))
          }
        } finally {
          if (token === latestLookup.current) {
            setChecking(false)
          }
        }
      },
      trimmed ? LOOKUP_DEBOUNCE_MS : 0
    )

    return () => clearTimeout(timer)
  }, [show, input, orderId])

  const target = preview?.course
  const blocked = !!preview?.blocked
  const canSubmit =
    !!target && !blocked && !!reason.trim() && !checking && !saving

  const handleSubmit = async () => {
    setSaving(true)
    setError('')
    try {
      let resp = await client.post(endpoints.changeCourse(), {
        orderId: orderId,
        coursenum: input.trim(),
        reason: reason.trim(),
      })
      // One dispatch refreshes the details box and every code row: reassignment changes
      // each code's assigned course, so updating only the order would leave the code list
      // showing the course they just moved away from.
      dispatch({
        type: 'updateOrderCodes',
        payload: { order: resp.data.order, codes: resp.data.codes },
      })
      toast.success(
        `Order moved to course ${resp.data.order.coursenum}. ` +
          `${resp.data.codesMoved} code(s) reassigned. Students will need a signup ` +
          `link for the new course.`
      )
      close()
    } catch (err) {
      setError(readError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <MEModal show={show} close={close} title="Change course for this order">
      <p className="mb-3">
        Current: <b>{coursenum}</b> ({courseTitle})
      </p>

      <div className="form-group">
        <label htmlFor="change_course_coursenum">
          New master course number
        </label>
        <input
          id="change_course_coursenum"
          type="text"
          className="form-control w-50"
          value={input}
          disabled={saving}
          onChange={e => setInput(e.target.value)}
        />
        <small className="form-text text-muted">
          Only master course numbers are accepted. Some masters are not listed
          in the catalog, so enter the number directly. Course numbers are case
          sensitive.
        </small>
        {checking && <div className="text-muted mt-1">Checking&hellip;</div>}
        {!checking && preview?.error && (
          <div className="text-danger mt-1">{preview.error}</div>
        )}
        {!checking && target && (
          <div className="text-success mt-1">
            <span className="fa-regular fa-check mr-2" />
            {target.coursenum} &mdash; {target.title}
            {preview.courseStatus !== 'published' && (
              <span className="text-warning ml-2">
                (status: {preview.courseStatus})
              </span>
            )}
          </div>
        )}
      </div>

      {blocked && (
        <div className="alert alert-danger" role="alert">
          {preview.blockReason}
          {!!preview.blockedCodes?.length && (
            <div className="mt-2">
              <small>Redeemed: {preview.blockedCodes.join(', ')}</small>
            </div>
          )}
        </div>
      )}

      {!blocked && target && (
        <div className="alert alert-warning" role="alert">
          <div>
            <b>
              {preview.numCodes} code{preview.numCodes === 1 ? '' : 's'}
            </b>{' '}
            will be reassigned to course {target.coursenum}. None have been
            redeemed.
          </div>
          {preview.numCodes !== preview.numCodesOrdered && (
            <div className="mt-1">
              This order was placed for {preview.numCodesOrdered} codes but has{' '}
              {preview.numCodes}.
            </div>
          )}
          {preview.currentAssignedCourses?.length > 1 && (
            <div className="mt-1">
              These codes are currently split across course{' '}
              {preview.currentAssignedCourses
                .map(c => `${c.coursenum || 'none'} (${c.count})`)
                .join(', ')}
              . All of them will be repointed.
            </div>
          )}
          {!!preview.numExpired && (
            <div className="mt-1">
              {preview.numExpired} of them{' '}
              {preview.numExpired === 1 ? 'is' : 'are'} expired and will stay
              expired.
            </div>
          )}
          {!!preview.numReturned && (
            <div className="mt-1">
              {preview.numReturned} {preview.numReturned === 1 ? 'is' : 'are'}{' '}
              marked returned.
            </div>
          )}
          {preview.sameFamily && (
            <div className="mt-1">
              Course {target.coursenum} shares a master course with {coursenum},
              so this will not change which book the codes unlock.
            </div>
          )}
          {!preview.hasVersion && (
            <div className="mt-1">
              Course {target.coursenum} has no book version set, so
              &ldquo;Send&rdquo; will fail until it does.
            </div>
          )}
          {!preview.royaltyCount && !!preview.currentRoyaltyCount && (
            <div className="mt-1">
              Course {target.coursenum} has no royalty agreements, so this order
              will stop contributing to royalty projections.
            </div>
          )}
          {!!preview.sentOn && (
            <div className="mt-1">
              Codes were already emailed to the customer under the old book
              title. Consider re-sending them.
            </div>
          )}
          <div className="mt-1">
            The price per code stays as it is &mdash; edit it separately if the
            new book is priced differently.
          </div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="change_course_reason">
          Reason (required, saved to the order and code notes)
        </label>
        <textarea
          id="change_course_reason"
          className="form-control"
          rows="2"
          maxLength="500"
          value={reason}
          disabled={saving}
          onChange={e => setReason(e.target.value)}
        />
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="text-right">
        <Button
          type="button"
          variant="secondary"
          className="btn-myed btn-default-myed mr-3"
          disabled={saving}
          onClick={close}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="danger"
          className="btn-myed btn-danger-myed"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {saving ? 'Changing…' : 'Change course'}
        </Button>
      </div>
    </MEModal>
  )
}
